import { computed, type ComputedRef, type Ref } from "vue";
import { resolveLocalizedTitle } from "./useCardData";
import { matrixScale } from "@/components/Survey/ResultsV2/analysisData";

export interface RadarRow {
  code: string;
  label: string;
  value: number;
  count: number;
  share: number;
}

export interface RadarItemStats {
  mean: number;
  n: number;
  skipped: number;
  rows: RadarRow[];
}

export interface RadarSeries {
  popId: string | number;
  label: string;
  color: string;
  values: (number | null)[];
  details: (RadarItemStats | null)[];
}

export interface RadarScale {
  min: number;
  max: number;
}

export interface RadarOptions {
  scale_mode?: "fit" | "full" | "manual";
  scale_min?: number | null;
  scale_max?: number | null;
  scale_codes?: string[];
  excluded_codes?: string[];
  reversed?: boolean;
}

interface MatrixItem {
  id: number;
  item_title?: unknown;
  item_name?: string;
}

interface MatrixPopulation {
  id: string | number;
  key?: string | null;
  label: string;
  color: string;
}

interface MatrixSpecs {
  matrix_type?: string;
  rating_limits?: { min?: number; max?: number };
  answers?: { code?: string; title?: unknown }[];
}

interface AnswerAggregate {
  nb?: number;
  weighted_nb?: number;
}

interface QuestionSlice {
  answers?: Record<string, AnswerAggregate>;
  skipped_respondents?: number;
  skipped_weighted_respondents?: number;
}

interface RadarContext {
  chartsData: Ref<Record<string | number, Record<string, unknown> | undefined>>;
  displayLocale: Ref<string | null | undefined>;
  isMatrix: Ref<boolean>;
  isMatrixMultiple: Ref<boolean>;
  activeItems: Ref<MatrixItem[] | null | undefined>;
  matrixPopulations: Ref<MatrixPopulation[]>;
  questionMeta: Ref<{ specs?: MatrixSpecs } | null | undefined>;
  radarOptions: Ref<RadarOptions>;
  codeLabels: Ref<Record<string, string>>;
  getAnswerCount: (answer: AnswerAggregate | undefined) => number;
  getSkippedCount: (source: QuestionSlice | null | undefined) => number;
}

export function useRadarChartData(ctx: RadarContext): {
  radarAxes: ComputedRef<string[]>;
  radarSeries: ComputedRef<RadarSeries[]>;
  radarScale: ComputedRef<RadarScale>;
} {
  const {
    chartsData,
    displayLocale,
    isMatrix,
    isMatrixMultiple,
    activeItems,
    matrixPopulations,
    questionMeta,
    radarOptions,
    codeLabels,
    getAnswerCount,
    getSkippedCount,
  } = ctx;

  const specs = computed<MatrixSpecs>(() => questionMeta.value?.specs || {});
  const isLikert = computed(() => isMatrix.value && !["rating", "nps"].includes(specs.value.matrix_type ?? ""));

  const radarAxes = computed<string[]>(() => {
    if (!isMatrix.value || isMatrixMultiple.value) return [];
    return (activeItems.value || []).map(
      (item) => resolveLocalizedTitle(item.item_title, displayLocale.value) || item.item_name || "",
    );
  });

  const likertPositions = computed<Map<string, number>>(() => {
    const positions = new Map<string, number>();
    if (!isLikert.value) return positions;
    const specCodes = (specs.value.answers || []).map((_, idx) => String(idx + 1));
    const saved = (radarOptions.value.scale_codes || []).filter((code) => specCodes.includes(code));
    const excluded = new Set(radarOptions.value.excluded_codes || []);
    const ordered = [...saved, ...specCodes.filter((code) => !saved.includes(code))].filter(
      (code) => !excluded.has(code),
    );
    ordered.forEach((code, idx) => {
      positions.set(code, radarOptions.value.reversed ? ordered.length - idx : idx + 1);
    });
    return positions;
  });

  function valueOf(code: string): number | null {
    if (code === "999") return null;
    if (isLikert.value) return likertPositions.value.get(code) ?? null;
    const value = Number(code);
    return Number.isNaN(value) ? null : value;
  }

  function statsOf(source: QuestionSlice | null | undefined): RadarItemStats | null {
    if (!source?.answers) return null;
    const rows: Omit<RadarRow, "share">[] = [];
    for (const [code, answer] of Object.entries(source.answers)) {
      const value = valueOf(code);
      if (value == null) continue;
      rows.push({ code, label: codeLabels.value[code] || code, value, count: getAnswerCount(answer) });
    }
    const n = rows.reduce((sum, row) => sum + row.count, 0);
    if (n <= 0) return null;
    rows.sort((a, b) => a.value - b.value);
    return {
      mean: rows.reduce((sum, row) => sum + row.value * row.count, 0) / n,
      n,
      skipped: getSkippedCount(source),
      rows: rows.map((row) => ({ ...row, share: row.count / n })),
    };
  }

  const radarSeries = computed<RadarSeries[]>(() => {
    if (!radarAxes.value.length) return [];
    return matrixPopulations.value
      .map((pop) => {
        const details = (activeItems.value || []).map((item) => {
          const itemData = chartsData.value[item.id];
          const slice = pop.key ? itemData?.[pop.key] : itemData;
          return statsOf(slice as QuestionSlice | undefined);
        });
        return {
          popId: pop.id,
          label: pop.label,
          color: pop.color,
          values: details.map((stats) => stats?.mean ?? null),
          details,
        };
      })
      .filter((serie) => serie.values.some((value) => value != null));
  });

  const baseScale = computed<RadarScale>(() =>
    isLikert.value ? { min: 1, max: Math.max(likertPositions.value.size, 2) } : matrixScale(specs.value),
  );

  const radarScale = computed<RadarScale>(() => {
    const base = baseScale.value;
    const { scale_mode: mode = "fit", scale_min: manualMin, scale_max: manualMax } = radarOptions.value;
    if (mode === "manual") {
      const min = manualMin ?? base.min;
      const max = manualMax ?? base.max;
      return max > min ? { min, max } : base;
    }
    if (mode === "full") return base;
    const values = radarSeries.value
      .flatMap((serie) => serie.values)
      .filter((value): value is number => value != null);
    if (!values.length) return base;
    const lo = Math.min(...values);
    const hi = Math.max(...values);
    const padding = Math.max((hi - lo) * 0.15, 0.25);
    return {
      min: Math.round(Math.max(base.min, lo - padding) * 10) / 10,
      max: Math.round(Math.min(base.max, hi + padding) * 10) / 10,
    };
  });

  return { radarAxes, radarSeries, radarScale };
}
